import { useCallback, useEffect, useState } from "react";
import {
  ProjectRecordCenterAgencyViewV1Schema,
  type ProjectRecordCenterAgencyViewV1,
} from "@enrein/shared-contracts";
import type { StudioApiClient } from "../../../api.js";
import { V10Btn } from "./V10Primitives.js";

function timelineStatusLabel(status: ProjectRecordCenterAgencyViewV1["timeline"][number]["status"]): string {
  switch (status) {
    case "complete":
      return "Complete";
    case "pending":
      return "In progress";
    case "blocked":
      return "Blocked";
    case "skipped":
      return "Skipped";
    default:
      return "In progress";
  }
}

function formatOperationalSections(sections: ProjectRecordCenterAgencyViewV1["sections"]): string[] {
  if (!sections) return [];
  const lines: string[] = [];
  if (sections.attachedUrl) lines.push(`Attached site: ${sections.attachedUrl}`);
  if (sections.scanStatus) lines.push(`Scan: ${sections.scanStatus}`);
  if (sections.importReviewStatus) lines.push(`Import review: ${sections.importReviewStatus}`);
  if (sections.importDecisionCount != null) {
    lines.push(`Import decisions: ${sections.importDecisionCount}`);
  }
  if (sections.hasBusinessGraphRevision != null) {
    lines.push(sections.hasBusinessGraphRevision ? "Business profile confirmed" : "Business profile pending");
  }
  return lines;
}

export function V10RecordCenterPanel({
  api,
  projectId,
}: {
  readonly api: StudioApiClient;
  readonly projectId: string;
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ProjectRecordCenterAgencyViewV1 | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const next = ProjectRecordCenterAgencyViewV1Schema.parse(await api.getProjectRecordCenter(projectId));
      setView(next);
    } catch (caught) {
      setView(null);
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
      setBusy(false);
    }
  }, [api, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !view) {
    return (
      <section className="v10-panel-block" aria-label="Record Center">
        <p className="v10-cl-kicker">Record Center</p>
        <p className="v10-muted">Loading delivery record…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="v10-panel-block" aria-label="Record Center">
        <p className="v10-cl-kicker">Record Center</p>
        <p className="v10-validation">{error}</p>
        <div className="v10-hero-actions">
          <V10Btn onClick={() => void load()} disabled={busy}>
            Retry
          </V10Btn>
        </div>
      </section>
    );
  }

  if (!view) {
    return (
      <section className="v10-panel-block" aria-label="Record Center">
        <p className="v10-cl-kicker">Record Center</p>
        <p className="v10-muted">Record not ready yet.</p>
      </section>
    );
  }

  const operationalLines = formatOperationalSections(view.sections);

  return (
    <section className="v10-panel-block" aria-label="Record Center">
      <p className="v10-cl-kicker">Record Center</p>
      <p>
        <strong>Status:</strong> {view.summary.currentStatus}
      </p>
      {view.summary.nextAction ? (
        <p className="v10-muted">
          <strong>Next:</strong> {view.summary.nextAction}
        </p>
      ) : null}
      {operationalLines.length > 0 ? (
        <ul className="v10-muted">
          {operationalLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      {view.documents.length > 0 ? (
        <>
          <p className="v10-cl-kicker">Proof documents</p>
          <ul>
            {view.documents.map((doc) => (
              <li key={`${doc.docType}-${doc.label}`}>
                {doc.label} — {doc.lifecycleStatus}
                {doc.available ? "" : " (not ready)"}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {view.timeline.length > 0 ? (
        <div className="v10-proof-ledger" aria-label="Record timeline">
          {view.timeline.map((entry) => (
            <div
              key={entry.entryId}
              className={`v10-proof-step${entry.status === "pending" ? " active" : ""}`}
            >
              <div className="v10-proof-step-left">
                <span className="v10-proof-dot" aria-hidden="true">
                  {entry.status === "complete" ? "✓" : "·"}
                </span>
                <div>
                  <strong>{entry.headline}</strong>
                  <p className="v10-muted">{entry.summary}</p>
                </div>
              </div>
              <span className="v10-muted">{timelineStatusLabel(entry.status)}</span>
            </div>
          ))}
        </div>
      ) : null}
      {view.handoff ? (
        <>
          <p className="v10-cl-kicker">Handoff</p>
          <p>{view.handoff.clientSummary}</p>
          <p className="v10-muted">
            {view.handoff.handoffAvailable ? "Handoff package available." : "Handoff not ready yet."}
          </p>
        </>
      ) : null}
      {view.warnings && view.warnings.length > 0 ? (
        <ul className="v10-validation-list">
          {view.warnings.map((warning) => (
            <li key={warning.code}>{warning.message}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
